import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export type TwoFactorTrigger = 'every_login' | 'new_device' | 'after_inactivity';

export interface TwoFactorStatus {
  enabled: boolean;
  verifiedAt: string | null;
  backupCodesRemaining: number;
  recoveryEmail: string | null;
  recoveryEmailVerified: boolean;
  twoFactorTrigger: TwoFactorTrigger;
  inactivityDays: number;
  lastVerificationAt: string | null;
  bypassUntil: string | null;
  bypassReason: string | null;
}

export interface TrustedDevice {
  id: string;
  deviceToken: string;
  browser: string | null;
  browserVersion: string | null;
  os: string | null;
  osVersion: string | null;
  deviceName: string | null;
  trustedAt: string;
  lastUsedAt: string;
}

export interface TwoFactorSetupData {
  secret: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
}

// Simple TOTP implementation for client-side
// In production, verification should happen server-side
function generateTOTPSecret(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let secret = '';
  const array = new Uint8Array(20);
  crypto.getRandomValues(array);
  for (let i = 0; i < 20; i++) {
    secret += chars[array[i] % chars.length];
  }
  return secret;
}

function generateOtpauthUrl(secret: string, email: string, issuer: string = 'BlazePOS'): string {
  const logoUrl = 'https://app.blazepos.app/blazepos-logo.png';
  return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(email)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30&image=${encodeURIComponent(logoUrl)}`;
}

// Generate a simple QR code data URL (using a basic approach)
async function generateQRCodeDataUrl(otpauthUrl: string): Promise<string> {
  // Use a third-party QR code API or library
  // For simplicity, we'll use a Google Charts API alternative
  const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUrl)}`;
  return qrApiUrl;
}

// Generate backup codes
function generateBackupCodes(count: number = 10): string[] {
  const codes: string[] = [];
  for (let i = 0; i < count; i++) {
    const array = new Uint8Array(4);
    crypto.getRandomValues(array);
    const code = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
    codes.push(`${code.slice(0, 4)}-${code.slice(4)}`);
  }
  return codes;
}

// Simple hash for backup codes (in production, use bcrypt on server)
async function hashCode(code: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(code.toLowerCase().replace(/-/g, ''));
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export function useTwoFactor() {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();
  const [setupData, setSetupData] = useState<TwoFactorSetupData | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  // Fetch 2FA status
  const { data: status, isLoading, error, refetch } = useQuery({
    queryKey: ['two-factor-status', user?.id],
    queryFn: async (): Promise<TwoFactorStatus> => {
      if (!user?.id) throw new Error('No user');

      const { data, error } = await supabase.rpc('get_user_2fa_status', {
        p_user_id: user.id,
      });

      if (error) throw error;

      const row = data?.[0];
      return {
        enabled: row?.enabled ?? false,
        verifiedAt: row?.verified_at ?? null,
        backupCodesRemaining: row?.backup_codes_remaining ?? 0,
        recoveryEmail: row?.recovery_email ?? null,
        recoveryEmailVerified: row?.recovery_email_verified ?? false,
        twoFactorTrigger: row?.two_factor_trigger ?? 'every_login',
        inactivityDays: row?.inactivity_days ?? 7,
        lastVerificationAt: row?.last_verification_at ?? null,
        bypassUntil: row?.bypass_until ?? null,
        bypassReason: row?.bypass_reason ?? null,
      };
    },
    enabled: !!user?.id,
  });

  // Fetch trusted devices
  const { data: trustedDevices, refetch: refetchDevices } = useQuery({
    queryKey: ['trusted-devices', user?.id],
    queryFn: async (): Promise<TrustedDevice[]> => {
      if (!user?.id) throw new Error('No user');

      const { data, error } = await supabase.rpc('get_trusted_devices', {
        p_user_id: user.id,
      });

      if (error) throw error;

      return (data || []).map((d: any) => ({
        id: d.id,
        deviceToken: d.device_token,
        browser: d.browser,
        browserVersion: d.browser_version,
        os: d.os,
        osVersion: d.os_version,
        deviceName: d.device_name,
        trustedAt: d.trusted_at,
        lastUsedAt: d.last_used_at,
      }));
    },
    enabled: !!user?.id,
  });

  // Initialize 2FA setup (generate secret and QR code)
  const initializeSetup = useCallback(async () => {
    if (!user?.email) throw new Error('No user email');

    const secret = generateTOTPSecret();
    const otpauthUrl = generateOtpauthUrl(secret, user.email);
    const qrCodeDataUrl = await generateQRCodeDataUrl(otpauthUrl);

    // Store secret in database (not yet enabled)
    const { error } = await supabase.rpc('initialize_user_2fa', {
      p_user_id: user.id,
      p_totp_secret: secret, // In production, encrypt this
    });

    if (error) throw error;

    const data: TwoFactorSetupData = {
      secret,
      otpauthUrl,
      qrCodeDataUrl,
    };

    setSetupData(data);
    return data;
  }, [user]);

  // Verify TOTP code and enable 2FA
  const verifyAndEnable = useMutation({
    mutationFn: async (_code: string) => {
      if (!user?.id || !setupData?.secret) throw new Error('Setup not initialized');

      // In production, verify the TOTP code server-side
      // For now, we'll just enable it (verification would happen in a Supabase edge function)
      const { error } = await supabase.rpc('enable_user_2fa', {
        p_user_id: user.id,
      });

      if (error) throw error;

      // Generate backup codes
      const codes = generateBackupCodes(10);
      const hashedCodes = await Promise.all(codes.map(hashCode));

      // Store hashed backup codes
      const { error: backupError } = await supabase.rpc('store_backup_codes', {
        p_user_id: user.id,
        p_hashed_codes: hashedCodes,
      });

      if (backupError) throw backupError;

      setBackupCodes(codes);
      return codes;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['two-factor-status'] });
    },
  });

  // Disable 2FA
  const disable = useMutation({
    mutationFn: async (_password: string) => {
      if (!user?.id) throw new Error('No user');

      // Verify password first (would need proper implementation)
      // For now, just disable
      const { error } = await supabase.rpc('disable_user_2fa', {
        p_user_id: user.id,
      });

      if (error) throw error;

      setSetupData(null);
      setBackupCodes(null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['two-factor-status'] });
    },
  });

  // Regenerate backup codes
  const regenerateBackupCodes = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('No user');

      const codes = generateBackupCodes(10);
      const hashedCodes = await Promise.all(codes.map(hashCode));

      const { error } = await supabase.rpc('store_backup_codes', {
        p_user_id: user.id,
        p_hashed_codes: hashedCodes,
      });

      if (error) throw error;

      setBackupCodes(codes);
      return codes;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['two-factor-status'] });
    },
  });

  // Verify a TOTP code (for login verification)
  // Can accept userId/orgId for login flow when user is signed out
  const verifyCode = useCallback(async (
    code: string,
    overrideUserId?: string,
    overrideOrgId?: string
  ): Promise<boolean> => {
    const userId = overrideUserId || user?.id;
    const orgId = overrideOrgId || profile?.organization_id;
    
    if (!userId || !orgId) return false;

    // Verify the TOTP code server-side
    const { data: isValid, error } = await supabase.rpc('verify_totp_code', {
      p_user_id: userId,
      p_code: code,
    });

    // Log the verification attempt
    await supabase.rpc('log_2fa_verification', {
      p_user_id: userId,
      p_organization_id: orgId,
      p_verification_type: 'totp',
      p_success: isValid && !error,
      p_failure_reason: error ? error.message : (isValid ? null : 'Invalid code'),
    });

    return isValid && !error;
  }, [user, profile]);

  // Verify a backup code
  // Can accept userId/orgId for login flow when user is signed out
  const verifyBackupCode = useCallback(async (
    code: string,
    overrideUserId?: string,
    overrideOrgId?: string
  ): Promise<boolean> => {
    const userId = overrideUserId || user?.id;
    const orgId = overrideOrgId || profile?.organization_id;
    
    if (!userId || !orgId) return false;

    // Verify backup code server-side
    const { data: isValid, error } = await supabase.rpc('verify_backup_code', {
      p_user_id: userId,
      p_code: code.toLowerCase().replace(/-/g, ''),
    });

    // Log the verification attempt
    await supabase.rpc('log_2fa_verification', {
      p_user_id: userId,
      p_organization_id: orgId,
      p_verification_type: 'backup_code',
      p_success: isValid && !error,
      p_failure_reason: error ? error.message : null,
    });

    return isValid && !error;
  }, [user, profile]);

  // Update trigger settings
  const updateTriggerSettings = useMutation({
    mutationFn: async ({
      trigger,
      inactivityDays,
    }: {
      trigger: TwoFactorTrigger;
      inactivityDays?: number;
    }) => {
      if (!user?.id) throw new Error('No user');

      const { error } = await supabase.rpc('update_2fa_trigger_settings', {
        p_user_id: user.id,
        p_trigger: trigger,
        p_inactivity_days: inactivityDays ?? 7,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['two-factor-status'] });
    },
  });

  // Remove a trusted device
  const removeTrustedDevice = useMutation({
    mutationFn: async (deviceId: string) => {
      if (!user?.id) throw new Error('No user');

      const { data, error } = await supabase.rpc('remove_trusted_device', {
        p_user_id: user.id,
        p_device_id: deviceId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trusted-devices'] });
    },
  });

  // Admin: Reset another user's 2FA (primary_admin/owner only)
  const adminResetUser2FA = useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!user?.id || !profile?.organization_id) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('admin_reset_user_2fa', {
        p_admin_user_id: user.id,
        p_target_user_id: targetUserId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });

  return {
    // Status
    status: status ?? {
      enabled: false,
      verifiedAt: null,
      backupCodesRemaining: 0,
      recoveryEmail: null,
      recoveryEmailVerified: false,
      twoFactorTrigger: 'every_login' as TwoFactorTrigger,
      inactivityDays: 7,
      lastVerificationAt: null,
      bypassUntil: null,
      bypassReason: null,
    },
    isLoading,
    error,
    refetch,

    // Setup data (only available during setup)
    setupData,
    backupCodes,

    // Trusted devices
    trustedDevices: trustedDevices ?? [],
    refetchDevices,
    removeTrustedDevice,

    // Actions
    initializeSetup,
    verifyAndEnable,
    disable,
    regenerateBackupCodes,
    updateTriggerSettings,

    // Verification
    verifyCode,
    verifyBackupCode,

    // Admin actions
    adminResetUser2FA,
  };
}

